/**
 * Search Radius utilities.
 *
 * Allow users to set a search radius filter (km or miles).
 * Converts radius + center point to bounding box for Sharetribe API.
 *
 * Different from geoRadius feature (which post-filters results with Haversine).
 * searchRadius adds a UI control to the search filters panel.
 *
 * Uses bounding box (bbox) approach for API compatibility.
 */

export const DEFAULT_RADIUS_KM = 25;
export const MAX_RADIUS_KM = 500;
export const MIN_RADIUS_KM = 1;
export const RADIUS_OPTIONS_KM = [5, 10, 25, 50, 100, 250, 500];

/**
 * Convert radius in km to degrees (approximate).
 * 1 degree latitude ≈ 111 km.
 * @param {number} radiusKm
 * @returns {number} degrees
 */
export const kmToDegrees = (radiusKm) => radiusKm / 111;

/**
 * Calculate bounding box from center + radius.
 * @param {{ lat: number, lng: number }} center
 * @param {number} radiusKm
 * @returns {{ ne: { lat, lng }, sw: { lat, lng } }}
 */
export const radiusToBbox = (center, radiusKm) => {
  const deg = kmToDegrees(radiusKm);
  const lngDeg = deg / Math.cos(center.lat * (Math.PI / 180));
  return {
    ne: { lat: center.lat + deg, lng: center.lng + lngDeg },
    sw: { lat: center.lat - deg, lng: center.lng - lngDeg },
  };
};

/**
 * Format radius for display.
 * @param {number} radiusKm
 * @param {boolean} [metric]
 * @returns {string}
 */
export const formatRadius = (radiusKm, metric = true) => {
  if (metric) return `${radiusKm} km`;
  return `${Math.round(radiusKm * 0.621)} mi`;
};

/**
 * Validate radius.
 * @param {number} radiusKm
 * @returns {string|null}
 */
export const validateRadius = (radiusKm) => {
  if (typeof radiusKm !== 'number' || isNaN(radiusKm)) return 'Radius must be a number';
  if (radiusKm < MIN_RADIUS_KM) return `Minimum radius is ${MIN_RADIUS_KM} km`;
  if (radiusKm > MAX_RADIUS_KM) return `Maximum radius is ${MAX_RADIUS_KM} km`;
  return null;
};
